import React, { useEffect, useState } from 'react';
import { Plus, KeyRound, UserX, UserCheck } from 'lucide-react';
import { adminFetch, fmtDate } from './api';
import { useAdminAuth } from './AuthContext';
import { PageHeader, EmptyState, ErrorBlock, Section, ScrollTable } from './widgets';

export default function AdminUsers() {
  const { user: me } = useAdminAuth();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', role: 'viewer' });
  const [createError, setCreateError] = useState(null);

  // Per-user inline password change
  const [editPwId, setEditPwId] = useState(null);
  const [pwInput, setPwInput] = useState('');
  const [pwError, setPwError] = useState(null);

  useEffect(() => { document.title = 'Sudco Admin — Users'; }, []);

  const load = async () => {
    setError(null);
    try {
      const d = await adminFetch('/api/admin-stats/users');
      setUsers(d.users);
    } catch (err) {
      setError(err);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreateError(null);
    setBusy(true);
    try {
      await adminFetch('/api/admin-stats/users', { method: 'POST', body: form });
      setForm({ email: '', password: '', role: 'viewer' });
      setShowCreate(false);
      load();
    } catch (err) {
      setCreateError(err);
    } finally {
      setBusy(false);
    }
  };

  const setRole = async (u, role) => {
    setBusy(true);
    try {
      await adminFetch(`/api/admin-stats/users/${u.id}/role`, { method: 'PATCH', body: { role } });
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const setDisabled = async (u, disabled) => {
    setBusy(true);
    try {
      await adminFetch(`/api/admin-stats/users/${u.id}/disabled`, { method: 'PATCH', body: { disabled } });
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const submitPassword = async (u) => {
    setPwError(null);
    setBusy(true);
    try {
      await adminFetch(`/api/admin-stats/users/${u.id}/password`, {
        method: 'PATCH',
        body: { password: pwInput },
      });
      setEditPwId(null);
      setPwInput('');
      load();
    } catch (err) {
      setPwError(err);
    } finally {
      setBusy(false);
    }
  };

  if (error) return <ErrorBlock error={error} onRetry={load} />;
  if (!users) return <div className="text-text-muted">Loading…</div>;

  return (
    <>
      <PageHeader
        title="Users"
        subtitle="Admins manage everyone here. Viewers can read the dashboard but can't manage users."
        right={
          <button
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center gap-1.5 bg-gradient-brand text-white text-sm px-3 py-1.5 rounded-lg hover:shadow-glow transition-shadow"
          >
            <Plus size={14} /> New user
          </button>
        }
      />

      {showCreate && (
        <form onSubmit={create} className="rounded-xl border border-border bg-surface p-4 mb-6 space-y-3">
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide text-text-muted mb-1">Email</label>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-bg border border-border rounded-lg px-3 py-2 text-text focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-text-muted mb-1">Password</label>
              <input
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="w-full bg-bg border border-border rounded-lg px-3 py-2 text-text focus:outline-none focus:border-accent"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-text-muted mb-1">Role</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full bg-bg border border-border rounded-lg px-3 py-2"
              >
                <option value="viewer">Viewer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          {createError && (
            <div className="text-sm text-red-400">{createError.message}</div>
          )}
          <div className="flex gap-2">
            <button type="submit" disabled={busy} className="bg-gradient-brand text-white text-sm px-4 py-1.5 rounded-lg disabled:opacity-60">
              Create
            </button>
            <button type="button" onClick={() => { setShowCreate(false); setCreateError(null); }} className="text-sm px-4 py-1.5 rounded-lg border border-border bg-surface-2">
              Cancel
            </button>
          </div>
        </form>
      )}

      <Section title="All users">
        {users.length === 0 ? (
          <EmptyState title="No users yet" />
        ) : (
          <ScrollTable>
            <table className="w-full text-sm">
              <thead className="bg-surface-2 text-text-muted text-xs uppercase tracking-wide sticky top-0 z-10">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Email</th>
                  <th className="text-left px-4 py-2.5 font-medium">Role</th>
                  <th className="text-left px-4 py-2.5 font-medium">Last login</th>
                  <th className="text-left px-4 py-2.5 font-medium">Status</th>
                  <th className="text-right px-4 py-2.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {users.map((u) => {
                  const isMe = me && u.id === me.id;
                  return (
                    <React.Fragment key={u.id}>
                      <tr className="hover:bg-surface-2/40">
                        <td className="px-4 py-2.5 text-text">
                          {u.email}
                          {isMe && <span className="ml-2 text-xs text-text-muted">(you)</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          <select
                            value={u.role}
                            onChange={(e) => setRole(u, e.target.value)}
                            disabled={busy || isMe}
                            className="bg-bg border border-border rounded-lg px-2 py-1 text-sm"
                          >
                            <option value="viewer">Viewer</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td className="px-4 py-2.5 text-text-muted">{fmtDate(u.last_login_at)}</td>
                        <td className="px-4 py-2.5">
                          {u.disabled_at
                            ? <span className="text-red-400 text-xs">Disabled</span>
                            : <span className="text-emerald-400 text-xs">Active</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="inline-flex gap-1">
                            <button
                              onClick={() => { setEditPwId(editPwId === u.id ? null : u.id); setPwInput(''); setPwError(null); }}
                              className="inline-flex items-center gap-1 text-xs text-text-muted hover:text-text px-2 py-1 rounded border border-border bg-surface-2"
                              title="Set password"
                            >
                              <KeyRound size={12} /> Password
                            </button>
                            {u.disabled_at ? (
                              <button
                                onClick={() => setDisabled(u, false)}
                                disabled={busy}
                                className="inline-flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 px-2 py-1 rounded border border-emerald-500/30 bg-emerald-500/10"
                              >
                                <UserCheck size={12} /> Enable
                              </button>
                            ) : (
                              <button
                                onClick={() => setDisabled(u, true)}
                                disabled={busy || isMe}
                                className="inline-flex items-center gap-1 text-xs text-red-400 hover:text-red-300 px-2 py-1 rounded border border-red-500/30 bg-red-500/10 disabled:opacity-40"
                                title={isMe ? "Can't disable yourself" : 'Disable user'}
                              >
                                <UserX size={12} /> Disable
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {editPwId === u.id && (
                        <tr className="bg-surface-2/40">
                          <td colSpan={5} className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <input
                                type="password"
                                placeholder="New password (min 8 chars)"
                                minLength={8}
                                value={pwInput}
                                onChange={(e) => setPwInput(e.target.value)}
                                className="bg-bg border border-border rounded-lg px-3 py-1.5 text-sm flex-1 max-w-sm"
                              />
                              <button
                                onClick={() => submitPassword(u)}
                                disabled={busy || pwInput.length < 8}
                                className="bg-gradient-brand text-white text-sm px-3 py-1.5 rounded-lg disabled:opacity-60"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => { setEditPwId(null); setPwInput(''); setPwError(null); }}
                                className="text-sm px-3 py-1.5 rounded-lg border border-border bg-surface"
                              >
                                Cancel
                              </button>
                              {pwError && <span className="text-xs text-red-400 ml-2">{pwError.message}</span>}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </ScrollTable>
        )}
      </Section>
    </>
  );
}
